import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'jspdf-autotable';

// Generate comprehensive PDF report with dendrograph
export const generateComprehensivePDF = async (solutionData, dendrographElementId) => {
  const pdf = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  
  // ==================== COVER PAGE ====================
  generateCoverPage(pdf, solutionData, pageWidth, pageHeight, margin);
  
  // ==================== TABLE OF CONTENTS ====================
  pdf.addPage();
  generateTableOfContents(pdf, solutionData, margin);
  
  // ==================== EXECUTIVE SUMMARY ====================
  pdf.addPage();
  generateExecutiveSummary(pdf, solutionData, margin, contentWidth);
  
  // ==================== DENROGRAPH VISUALIZATION ====================
  pdf.addPage();
  await generateDendrographPage(pdf, dendrographElementId, margin, contentWidth, pageHeight);
  
  // ==================== COMPONENT DETAILS ====================
  generateComponentDetails(pdf, solutionData, margin, contentWidth, pageHeight);
  
  // ==================== COST ANALYSIS ====================
  pdf.addPage();
  generateCostAnalysis(pdf, solutionData, margin, contentWidth);
  
  // ==================== COMPATIBILITY MATRIX ====================
  pdf.addPage();
  generateCompatibilityMatrix(pdf, solutionData, margin, contentWidth);
  
  // ==================== VALIDATION REPORT ====================
  pdf.addPage();
  generateValidationReport(pdf, solutionData, margin, contentWidth);
  
  // ==================== RECOMMENDATIONS ====================
  pdf.addPage();
  generateRecommendations(pdf, solutionData, margin, contentWidth);
  
  // ==================== FOOTER ====================
  addFooterToAllPages(pdf, solutionData, pageWidth, pageHeight);
  
  // Save PDF
  const filename = `${solutionData.name.toLowerCase().replace(/\s+/g, '-')}-architecture-report.pdf`;
  pdf.save(filename);
};

// Generate Cover Page
const generateCoverPage = (pdf, solutionData, pageWidth, pageHeight, margin) => {
  // Background
  pdf.setFillColor(30, 64, 175);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Decorative circles
  pdf.setFillColor(59, 130, 246);
  pdf.circle(pageWidth - 30, 30, 40, 'F');
  pdf.setFillColor(37, 99, 235);
  pdf.circle(pageWidth - 60, 80, 25, 'F');
  pdf.setFillColor(29, 78, 216);
  pdf.circle(30, pageHeight - 30, 35, 'F');
  
  // Title
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(36);
  pdf.setFont('helvetica', 'bold');
  pdf.text('AI Solution Architecture', pageWidth / 2, 100, { align: 'center' });
  
  // Subtitle
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'normal');
  pdf.text(solutionData.name || 'Untitled Solution', pageWidth / 2, 120, { align: 'center' });
  
  // Description
  if (solutionData.description) {
    pdf.setFontSize(12);
    pdf.text(solutionData.description, pageWidth / 2, 135, { align: 'center', maxWidth: 200 });
  }
  
  // Date
  pdf.setFontSize(11);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 160, { align: 'center' });
  
  // Stats
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  const stats = [
    `${solutionData.components.length} Components`,
    `${new Set(solutionData.components.map(c => c.layerId)).size} Layers`,
    `$${solutionData.totalCost.low.toFixed(0)} - $${solutionData.totalCost.high.toFixed(0)}`
  ];
  
  let yPos = 180;
  stats.forEach(stat => {
    pdf.text(stat, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;
  });
};

// Generate Table of Contents
const generateTableOfContents = (pdf, solutionData, margin) => {
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Table of Contents', margin, 30);
  
  const sections = [
    '1. Executive Summary',
    '2. Solution Architecture Visualization',
    '3. Component Details',
    '4. Cost Analysis',
    '5. Compatibility Matrix',
    '6. Validation Report',
    '7. Recommendations'
  ];
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);
  
  let yPos = 50;
  sections.forEach(section => {
    pdf.text(section, margin + 10, yPos);
    yPos += 15;
  });
};

// Generate Executive Summary
const generateExecutiveSummary = (pdf, solutionData, margin, contentWidth) => {
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Executive Summary', margin, 30);
  
  pdf.setDrawColor(30, 64, 175);
  pdf.setLineWidth(1);
  pdf.line(margin, 35, margin + contentWidth, 35);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);
  
  let yPos = 50;
  
  // Solution Overview
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Solution Overview', margin, yPos);
  yPos += 10;
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  const overviewText = `This AI solution architecture consists of ${solutionData.components.length} components ` +
    `organized across ${new Set(solutionData.components.map(c => c.phaseId)).size} phases, ` +
    `${new Set(solutionData.components.map(c => c.layerId)).size} layers, and ` +
    `${new Set(solutionData.components.map(c => c.subLayerId)).size} sub-layers.`;
  
  const overviewLines = pdf.splitTextToSize(overviewText, contentWidth - 10);
  pdf.text(overviewLines, margin, yPos);
  yPos += (overviewLines.length * 6) + 10;
  
  // Key Statistics
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Key Statistics', margin, yPos);
  yPos += 10;
  
  const stats = [
    ['Total Components', solutionData.components.length],
    ['Phases Covered', new Set(solutionData.components.map(c => c.phaseId)).size],
    ['Layers Covered', new Set(solutionData.components.map(c => c.layerId)).size],
    ['Sub-Layers Covered', new Set(solutionData.components.map(c => c.subLayerId)).size],
    ['Estimated Cost Range', `$${solutionData.totalCost.low.toFixed(2)} - $${solutionData.totalCost.high.toFixed(2)}`],
    ['Validation Status', solutionData.validation?.isValid ? 'Valid' : 'Has Issues']
  ];
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  
  stats.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label, margin, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value.toString(), margin + 80, yPos);
    yPos += 8;
  });
  
  yPos += 10;
  
  // Phase Breakdown
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Phase Breakdown', margin, yPos);
  yPos += 10;
  
  const phaseCounts = {};
  solutionData.components.forEach(comp => {
    const key = comp.phaseName || 'Unknown';
    phaseCounts[key] = (phaseCounts[key] || 0) + 1;
  });
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  
  Object.entries(phaseCounts).forEach(([phase, count]) => {
    pdf.text(`${phase}:`, margin, yPos);
    pdf.text(`${count} components`, margin + 80, yPos);
    yPos += 8;
  });
};

// Generate Dendrograph Page
const generateDendrographPage = async (pdf, dendrographElementId, margin, contentWidth, pageHeight) => {
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Solution Architecture Visualization', margin, 30);
  
  pdf.setDrawColor(30, 64, 175);
  pdf.setLineWidth(1);
  pdf.line(margin, 35, margin + contentWidth, 35);
  
  try {
    const element = document.getElementById(dendrographElementId);
    if (element) {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Check if image fits on one page
      const maxHeight = pageHeight - 60;
      let finalHeight = imgHeight;
      
      if (imgHeight > maxHeight) {
        finalHeight = maxHeight;
      }
      
      pdf.addImage(imgData, 'PNG', margin, 45, imgWidth, finalHeight);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Figure 1: Hierarchical dendrograph showing solution architecture', margin, 45 + finalHeight + 10);
    }
  } catch (error) {
    console.error('Error generating dendrograph image:', error);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(255, 0, 0);
    pdf.text('Dendrograph visualization could not be rendered.', margin, 50);
  }
};

// Generate Component Details
const generateComponentDetails = (pdf, solutionData, margin, contentWidth, pageHeight) => {
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Component Details', margin, 30);
  
  pdf.setDrawColor(30, 64, 175);
  pdf.setLineWidth(1);
  pdf.line(margin, 35, margin + contentWidth, 35);
  
  let yPos = 50;
  let pageNum = pdf.internal.getNumberOfPages();
  
  // Group components by phase
  const phases = {};
  solutionData.components.forEach(comp => {
    const phaseKey = comp.phaseName || 'Unknown Phase';
    if (!phases[phaseKey]) {
      phases[phaseKey] = [];
    }
    phases[phaseKey].push(comp);
  });
  
  Object.entries(phases).forEach(([phaseName, components]) => {
    // Check if we need a new page
    if (yPos > pageHeight - 40) {
      pdf.addPage();
      yPos = 40;
    }
    
    // Phase header
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text(phaseName, margin, yPos);
    yPos += 10;
    
    components.forEach(comp => {
      // Check if we need a new page
      if (yPos > pageHeight - 60) {
        pdf.addPage();
        yPos = 40;
      }
      
      // Component box
      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, yPos - 5, contentWidth, 45, 'F');
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(`${comp.icon || '🔧'} ${comp.name}`, margin + 5, yPos);
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      
      pdf.text(`Layer: ${comp.layerName}`, margin + 5, yPos + 8);
      pdf.text(`Sub-Layer: ${comp.subLayerName}`, margin + 5, yPos + 16);
      pdf.text(`Purpose: ${comp.purpose}`, margin + 5, yPos + 24);
      pdf.text(`Function: ${comp.function}`, margin + 5, yPos + 32);
      
      pdf.setTextColor(30, 64, 175);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Cost: $${comp.cost?.low || 0} - $${comp.cost?.high || 0} ${comp.cost?.unit || ''}`, margin + contentWidth - 60, yPos);
      
      yPos += 50;
    });
    
    yPos += 10;
  });
};

// Generate Cost Analysis
const generateCostAnalysis = (pdf, solutionData, margin, contentWidth) => {
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Cost Analysis', margin, 30);
  
  pdf.setDrawColor(30, 64, 175);
  pdf.setLineWidth(1);
  pdf.line(margin, 35, margin + contentWidth, 35);
  
  // Summary
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text('Cost Summary', margin, 50);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Total Estimated Cost: $${solutionData.totalCost.low.toFixed(2)} - $${solutionData.totalCost.high.toFixed(2)} USD`, margin, 65);
  pdf.text(`Average Cost: $${((solutionData.totalCost.low + solutionData.totalCost.high) / 2).toFixed(2)} USD`, margin, 75);
  
  // Cost breakdown table
  if (solutionData.totalCost.breakdown && solutionData.totalCost.breakdown.length > 0) {
    const tableData = solutionData.totalCost.breakdown.map(item => [
      item.component,
      `$${item.low.toFixed(2)}`,
      `$${item.high.toFixed(2)}`,
      item.unit
    ]);
    
    pdf.autoTable({
      startY: 90,
      head: [['Component', 'Low Cost', 'High Cost', 'Unit']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [30, 64, 175],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      margin: { left: margin, right: margin }
    });
  }
};

// Generate Compatibility Matrix
const generateCompatibilityMatrix = (pdf, solutionData, margin, contentWidth) => {
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Compatibility Matrix', margin, 30);
  
  pdf.setDrawColor(30, 64, 175);
  pdf.setLineWidth(1);
  pdf.line(margin, 35, margin + contentWidth, 35);
  
  const components = solutionData.components;
  
  if (components.length <= 15) {
    // Build matrix table
    const tableData = components.map((comp1, i) => [
      `${comp1.icon} ${comp1.name}`,
      ...components.map((comp2, j) => {
        if (i === j) return '—';
        return checkCompatibility(comp1, comp2) ? '✓' : '✗';
      })
    ]);
    
    const headers = ['Component', ...components.map(c => c.name.substring(0, 10))];
    
    pdf.autoTable({
      startY: 50,
      head: [headers],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [30, 64, 175],
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 7,
        cellPadding: 2,
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      margin: { left: margin, right: margin }
    });
  } else {
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text(`Compatibility matrix available for ${components.length} components (showing summary only due to size)`, margin, 50);
    
    const compatiblePairs = [];
    for (let i = 0; i < components.length; i++) {
      for (let j = i + 1; j < components.length; j++) {
        if (checkCompatibility(components[i], components[j])) {
          compatiblePairs.push([components[i].name, components[j].name]);
        }
      }
    }
    
    pdf.autoTable({
      startY: 65,
      head: [['Component A', 'Component B']],
      body: compatiblePairs,
      theme: 'grid',
      headStyles: {
        fillColor: [30, 64, 175],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3
      },
      margin: { left: margin, right: margin }
    });
  }
};

// Generate Validation Report
const generateValidationReport = (pdf, solutionData, margin, contentWidth) => {
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Validation Report', margin, 30);
  
  pdf.setDrawColor(30, 64, 175);
  pdf.setLineWidth(1);
  pdf.line(margin, 35, margin + contentWidth, 35);
  
  let yPos = 50;
  
  if (solutionData.validation) {
    // Status
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    if (solutionData.validation.isValid) {
      pdf.setTextColor(0, 128, 0);
      pdf.text('✓ Solution is VALID', margin, yPos);
    } else {
      pdf.setTextColor(255, 0, 0);
      pdf.text('✗ Solution has ISSUES', margin, yPos);
    }
    yPos += 15;
    
    // Issues
    if (solutionData.validation.issues && solutionData.validation.issues.length > 0) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 0, 0);
      pdf.text('Issues:', margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      solutionData.validation.issues.forEach(issue => {
        const lines = pdf.splitTextToSize(`• ${issue}`, contentWidth - 10);
        pdf.text(lines, margin + 5, yPos);
        yPos += (lines.length * 6) + 4;
      });
      yPos += 10;
    }
    
    // Warnings
    if (solutionData.validation.warnings && solutionData.validation.warnings.length > 0) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 165, 0);
      pdf.text('Warnings:', margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      solutionData.validation.warnings.forEach(warning => {
        const lines = pdf.splitTextToSize(`• ${warning}`, contentWidth - 10);
        pdf.text(lines, margin + 5, yPos);
        yPos += (lines.length * 6) + 4;
      });
    }
  }
};

// Generate Recommendations
const generateRecommendations = (pdf, solutionData, margin, contentWidth) => {
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Recommendations', margin, 30);
  
  pdf.setDrawColor(30, 64, 175);
  pdf.setLineWidth(1);
  pdf.line(margin, 35, margin + contentWidth, 35);
  
  let yPos = 50;
  
  const recommendations = getRecommendations(solutionData);
  
  recommendations.forEach((rec, index) => {
    if (yPos > 250) {
      pdf.addPage();
      yPos = 40;
    }
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text(`${index + 1}. ${rec.title}`, margin, yPos);
    yPos += 8;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    const lines = pdf.splitTextToSize(rec.description, contentWidth - 10);
    pdf.text(lines, margin + 5, yPos);
    yPos += (lines.length * 6) + 8;
    
    if (rec.priority) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Priority: ${rec.priority}`, margin + 5, yPos);
      yPos += 10;
    }
  });
};

// Get recommendations
const getRecommendations = (solutionData) => {
  const recommendations = [];
  
  // Check for missing layers
  const coveredLayers = new Set(solutionData.components.map(c => c.layerId));
  const essentialLayers = ['layer1', 'layer3', 'layer4', 'layer6', 'layer8', 'layer10', 'layer11'];
  
  essentialLayers.forEach(layerId => {
    if (!coveredLayers.has(layerId)) {
      const layerNames = {
        'layer1': 'Physical Substrate & Compute',
        'layer3': 'Data Acquisition & Generation',
        'layer4': 'Data Structuring & Storage',
        'layer6': 'Model Architecture & Learning Paradigm',
        'layer8': 'Compilation, Packaging & Inference Engine',
        'layer10': 'Application Logic & User Interface',
        'layer11': 'Observability, Evaluation & Security'
      };
      recommendations.push({
        title: `Add components to ${layerNames[layerId]}`,
        description: `Your solution is missing components from the ${layerNames[layerId]} layer. This may affect the completeness of your AI solution.`,
        priority: 'High'
      });
    }
  });
  
  // Check cost
  if (solutionData.totalCost.high > 100000) {
    recommendations.push({
      title: 'Cost Optimization',
      description: 'Your estimated cost exceeds $100,000. Consider using managed services or edge deployment to reduce costs.',
      priority: 'Medium'
    });
  }
  
  // Check security
  if (!coveredLayers.has('layer11')) {
    recommendations.push({
      title: 'Add Security Components',
      description: 'Your solution lacks security and observability components. Consider adding encryption, monitoring, and drift detection.',
      priority: 'High'
    });
  }
  
  return recommendations;
};

// Check compatibility between components
const checkCompatibility = (comp1, comp2) => {
  if (!comp1?.tags || !comp2?.tags) return false;
  const tags1 = new Set(comp1.tags);
  const tags2 = new Set(comp2.tags);
  for (const tag of tags1) {
    if (tags2.has(tag)) return true;
  }
  return false;
};

// Add footer to all pages
const addFooterToAllPages = (pdf, solutionData, pageWidth, pageHeight) => {
  const pageCount = pdf.internal.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    
    // Footer line
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(15, pageHeight - 15, pageWidth - 15, pageHeight - 15);
    
    // Footer text
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(150, 150, 150);
    pdf.text(solutionData.name || 'AI Solution', 15, pageHeight - 8);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 15, pageHeight - 8, { align: 'right' });
  }
};

// Export simplified functions for backward compatibility
export const generatePDF = async (elementId, filename = 'ai-solution.pdf') => {
  const element = document.getElementById(elementId);
  if (!element) return;
  
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff'
  });
  
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF('landscape', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
  
  pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
  pdf.save(filename);
};

export const generateTextPDF = (solutionData, filename = 'ai-solution-report.pdf') => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 20;
  let y = 30;
  
  pdf.setFontSize(20);
  pdf.text('AI Solution Architecture Report', margin, y);
  y += 10;
  
  pdf.setFontSize(12);
  pdf.text(`Solution: ${solutionData.name}`, margin, y);
  y += 8;
  pdf.text(`Created: ${new Date(solutionData.createdAt).toLocaleString()}`, margin, y);
  y += 15;
  
  pdf.setFontSize(16);
  pdf.text('Components', margin, y);
  y += 10;
  
  pdf.setFontSize(11);
  solutionData.components.forEach(comp => {
    if (y > 270) {
      pdf.addPage();
      y = 20;
    }
    pdf.text(`• ${comp.name}`, margin, y);
    y += 6;
    pdf.text(`  ${comp.purpose}`, margin + 5, y);
    y += 6;
    pdf.text(`  Cost: $${comp.cost.low} - $${comp.cost.high} ${comp.cost.unit}`, margin + 5, y);
    y += 10;
  });
  
  pdf.save(filename);
};